/**
 * DesignerPalette - Element creation buttons
 *
 * A simple toolbar for adding new elements to the canvas.
 */

import { memo, useCallback } from 'react';
import type { DesignerElementType } from '@deep-agents/shared-types';

interface DesignerPaletteProps {
  onAddElement: (type: DesignerElementType) => void;
}

// Button definitions
const paletteItems: { type: DesignerElementType; label: string; icon: React.ReactNode }[] = [
  {
    type: 'text',
    label: 'Text',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h8m-8 6h16" />
      </svg>
    ),
  },
  {
    type: 'image',
    label: 'Image',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    type: 'button',
    label: 'Button',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
      </svg>
    ),
  },
];

export const DesignerPalette = memo(({ onAddElement }: DesignerPaletteProps) => {
  const handleClick = useCallback((type: DesignerElementType) => {
    onAddElement(type);
  }, [onAddElement]);

  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-slate-100/80 rounded-lg border border-slate-200/60">
      {paletteItems.map((item) => (
        <button
          key={item.type}
          type="button"
          onClick={() => handleClick(item.type)}
          className="
            flex items-center gap-1.5 px-2.5 py-1.5
            text-xs font-medium text-slate-600
            rounded-md hover:bg-white hover:text-indigo-600 hover:shadow-sm
            transition-all
          "
          title={`Add ${item.label}`}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
});

DesignerPalette.displayName = 'DesignerPalette';
